import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SkillLevel {
  level: string;
  description: string;
  videoPath?: string;
  color: string;
  badge: string;
}

export function HowWeDoItSection() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  });
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const skillLevels: SkillLevel[] = [
    {
      level: "Starter",
      description: "Master the fundamentals with basic touches and footwork",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Step-over_FutWork_Desktop_U8_U9_U10.mp4",
      color: "green",
      badge: "Level 1"
    },
    {
      level: "Intermediate",
      description: "Advance your technique with step-overs and dynamic ball control",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/L-turns_Desktop_U8_U9_U10_V2.mp4",
      color: "orange",
      badge: "Level 2"
    },
    {
      level: "Strength",
      description: "Build power and explosiveness with targeted strength drills",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Side_Lunges_Right_Strong_Like_a_Pro_Desktop.mp4",
      color: "slate",
      badge: "Specialty"
    },
    {
      level: "Stability",
      description: "Develop balance and core control for better on-field performance",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Ball_Drop_Right_Grab_Quick_Feet_Masters_Desktop.mp4",
      color: "blue",
      badge: "Specialty"
    },
    {
      level: "Speed",
      description: "Enhance quickness and acceleration with high-intensity footwork",
      videoPath: "https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Lightning_Line_Jumps_Blast-Off-Boosters_Desktop.mp4",
      color: "yellow",
      badge: "Specialty"
    }
  ];

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const playNextVideo = useCallback((currentIndex: number) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      currentVideo.pause();
      currentVideo.currentTime = 0;
    }

    const nextIndex = (currentIndex + 1) % skillLevels.length;
    const nextVideo = videoRefs.current[nextIndex];
    
    if (nextVideo) {
      nextVideo.currentTime = 0;
      nextVideo.play();
      setActiveVideoIndex(nextIndex);
      
      if (emblaApi) {
        emblaApi.scrollTo(nextIndex);
      }

      timerRef.current = setTimeout(() => {
        playNextVideo(nextIndex);
      }, 5000);
    }
  }, [emblaApi, skillLevels.length]);

  const handleVideoClick = (index: number) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    const clickedVideo = videoRefs.current[index];
    if (clickedVideo) {
      if (clickedVideo.paused) {
        clickedVideo.currentTime = 0;
        clickedVideo.play();
        setActiveVideoIndex(index);
        
        timerRef.current = setTimeout(() => {
          playNextVideo(index);
        }, 5000);
      } else {
        clickedVideo.pause();
        setActiveVideoIndex(null);
      }
    }
  };

  const handleVideoPlay = (index: number) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setActiveVideoIndex(index);
  };

  const handleVideoPause = (index: number) => {
    if (activeVideoIndex === index) {
      setActiveVideoIndex(null);
    }
  };

  useEffect(() => {
    const firstVideo = videoRefs.current[0];
    if (firstVideo) {
      firstVideo.currentTime = 0;
      firstVideo.play();
      setActiveVideoIndex(0);
      
      timerRef.current = setTimeout(() => {
        playNextVideo(0);
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      videoRefs.current.forEach(video => {
        if (video) video.pause();
      });
    };
  }, [playNextVideo]);

  const getColorClass = (color: string) => {
    const colors: Record<string, string> = {
      green: 'bg-emerald-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      slate: 'bg-slate-600',
      blue: 'bg-blue-500',
      yellow: 'bg-amber-500',
      cyan: 'bg-cyan-500'
    };
    return colors[color] || 'bg-primary';
  };

  return (
    <section className="py-8 md:py-14 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header - Compact */}
        <div className="text-center mb-6 md:mb-12">
          <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium mb-3">
            Training Levels
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
            How We Do It
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
            Progressive skill development from fundamentals to advanced
          </p>
        </div>

        {/* Navigation Arrows - Desktop only */}
        <div className="relative">
          <Button
            variant="outline"
            size="icon"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background/80 backdrop-blur-sm shadow-lg hidden md:flex disabled:opacity-0"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background/80 backdrop-blur-sm shadow-lg hidden md:flex disabled:opacity-0"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          {/* Carousel - Edge to edge on mobile */}
          <div className="overflow-hidden -mx-4 px-4 md:mx-12 md:px-0" ref={emblaRef}>
            <div className="flex gap-3 md:gap-5">
              {skillLevels.map((skill, index) => (
                <div
                  key={index}
                  className="flex-[0_0_72%] sm:flex-[0_0_55%] md:flex-[0_0_40%] lg:flex-[0_0_30%] min-w-0"
                >
                  <div className="group">
                    <div className={`relative aspect-[3/4] rounded-xl md:rounded-2xl overflow-hidden shadow-lg cursor-pointer transition-all duration-300 active:scale-[0.98] ${
                      activeVideoIndex === index 
                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-[1.01]' 
                        : ''
                    }`}>
                      {skill.videoPath && (
                        <>
                          <video
                            ref={(el) => (videoRefs.current[index] = el)}
                            src={skill.videoPath}
                            muted
                            playsInline
                            onClick={() => handleVideoClick(index)}
                            onPlay={() => handleVideoPlay(index)}
                            onPause={() => handleVideoPause(index)}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          
                          {/* Play Icon Overlay */}
                          <div className={`absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none transition-opacity duration-300 ${
                            activeVideoIndex === index ? 'opacity-0' : 'opacity-100'
                          }`}>
                            <div className="bg-white/90 rounded-full p-3 md:p-4 shadow-lg">
                              <Play className="h-6 w-6 md:h-8 md:w-8 text-foreground fill-foreground" />
                            </div>
                          </div>
                        </>
                      )}

                      {/* Level Badge */}
                      <div className={`absolute top-2.5 left-2.5 px-2.5 py-0.5 ${getColorClass(skill.color)} rounded-full text-white text-[10px] md:text-xs font-semibold shadow-md`}>
                        {skill.badge}
                      </div>
                    </div>

                    {/* Text Below Video */}
                    <div className="mt-3 md:mt-4">
                      <h3 className="text-base md:text-lg font-bold text-foreground mb-0.5">
                        {skill.level}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground leading-snug">
                        {skill.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-1.5 mt-5 md:mt-8">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`h-1.5 md:h-2 rounded-full transition-all duration-300 ${
                index === selectedIndex 
                  ? 'bg-primary w-6 md:w-8' 
                  : 'bg-border w-1.5 md:w-2 hover:bg-muted-foreground/50'
              }`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
